import { useCallback, useState } from "react";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import { polaris } from "../lib/polaris-client";

export type ContactSubmissionType = "contact" | "demo";

export type ContactSubmissionInput = {
  name: string;
  email: string;
  message: string;
  organization?: string;
  product?: "opero" | "qeep" | "secura" | "governis" | "nexdoc";
  type?: ContactSubmissionType;
  sourceUrl?: string;
};

export type ContactSubmissionStatus = "idle" | "sending" | "sent" | "error";

export function useContactSubmission(autoResetMs = 4000) {
  const [status, setStatus] = useState<ContactSubmissionStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
  }, []);

  const submit = useCallback(
    async (input: ContactSubmissionInput) => {
      setStatus("sending");
      setError(null);
      try {
        await polaris.contactSubmissions.create({
          body: {
            name: input.name,
            email: input.email,
            message: input.message,
            organization: input.organization || undefined,
            product: input.product ?? "opero",
            type: input.type ?? "contact",
            sourceUrl: input.sourceUrl ?? window.location.href,
          },
        });
        setStatus("sent");
        if (autoResetMs > 0) {
          setTimeout(() => setStatus("idle"), autoResetMs);
        }
        return true;
      } catch (err) {
        setError(getErrorMessage(err, "Failed to send message"));
        setStatus("error");
        if (autoResetMs > 0) {
          setTimeout(() => setStatus("idle"), autoResetMs);
        }
        return false;
      }
    },
    [autoResetMs],
  );

  return { status, error, submit, reset };
}
